/**
 * Kural zinciri takibi — durum makinesi.
 *
 * Çocuğa bir nesne (üstünde sayı olan bir şekil) ve numaralı kurallar
 * veriliyor. Her kural bir koşul soruyor, koşulun cevabına göre bir
 * şey yapıp başka bir kurala gönderiyor. Nesnenin çıkıştaki hâli
 * soruluyor.
 *
 * SONSUZ DÖNGÜ YOK, ÇÜNKÜ GERİYE ATLAMA YOK. Her kural yalnızca
 * KENDİNDEN SONRAKİ bir kurala ya da çıkışa gönderiyor. Bu, yönlü
 * çevrimsiz çizge (DAG) kuralının en dar hâli ve döngü olasılığını
 * sıfırlıyor — "üretip sonra döngü var mı diye bak" demeye gerek
 * kalmıyor.
 *
 * SAYI SINIRLARI ÜRETİMDE TUTULUYOR. Çarpma ve çıkarma, sayıyı
 * kolayca üç haneye çıkarıyor ya da eksiye düşürüyor. İkisi de
 * görevin ölçtüğü şeyi değiştirir: biri çarpma becerisi sınavına,
 * öteki "eksi sayı olur mu" sorusuna dönüşür. Kurallar üretildikten
 * sonra izlenip sınırı aşan eylem yerinde ONARILIYOR — eleyip baştan
 * üretmek yerine. Aynı ders `arith_grid` ve `rule_maze`'de de
 * öğrenilmişti.
 *
 * RENK YOK, ŞEKİL VAR. İstek listesindeki örnek "kırmızı top"
 * üzerineydi. Renk tek başına bilgi taşıyamaz (gorsellik.md §0.1):
 * gri tonlamalı bir yazıcıda kırmızı top ile mavi top aynı şey olur
 * ve sayfanın yarısı okunmaz hale gelir.
 */

import { Rng } from '../../prng'
import { foldTr } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult, RenderNode } from '../types'
import type { ShapeName } from '../../render/shapes'
import { SEKIL_ADI } from '../../render/shapes'

const AKIS_SEKIL: ShapeName[] = ['daire_bos', 'kare_bos', 'ucgen_bos']

type Kosul =
  | { tur: 'sekil'; sekil: ShapeName }
  | { tur: 'buyuk'; esik: number }
  | { tur: 'cift' }

type Eylem =
  | { tur: 'carp'; k: number }
  | { tur: 'ekle'; k: number }
  | { tur: 'cikar'; k: number }
  | { tur: 'sekil'; sekil: ShapeName }
  | { tur: 'yok' }

interface Kural {
  kosul: Kosul
  /** Koşul doğruysa: eylem ve gidilecek kural (0 = çıkış) */
  evet: { eylem: Eylem; hedef: number }
  hayir: { eylem: Eylem; hedef: number }
}

interface Durum {
  sayi: number
  sekil: ShapeName
}

function kosulMetni(k: Kosul): string {
  if (k.tur === 'sekil') return `şekil ${SEKIL_ADI[k.sekil]} ise`
  if (k.tur === 'buyuk') return `sayı ${k.esik}'den büyükse`
  return 'sayı çift ise'
}

function eylemMetni(e: Eylem): string {
  switch (e.tur) {
    case 'carp':
      return `sayıyı ${e.k} ile çarp`
    case 'ekle':
      return `sayıya ${e.k} ekle`
    case 'cikar':
      return `sayıdan ${e.k} çıkar`
    case 'sekil':
      return `şekli ${SEKIL_ADI[e.sekil]} yap`
    case 'yok':
      return 'hiçbir şey yapma'
  }
}

function hedefMetni(h: number): string {
  return h === 0 ? 'bitir' : `${h}. kurala git`
}

function kosulDogru(k: Kosul, d: Durum): boolean {
  if (k.tur === 'sekil') return d.sekil === k.sekil
  if (k.tur === 'buyuk') return d.sayi > k.esik
  return d.sayi % 2 === 0
}

function uygula(e: Eylem, d: Durum): Durum {
  switch (e.tur) {
    case 'carp':
      return { ...d, sayi: d.sayi * e.k }
    case 'ekle':
      return { ...d, sayi: d.sayi + e.k }
    case 'cikar':
      return { ...d, sayi: d.sayi - e.k }
    case 'sekil':
      return { ...d, sekil: e.sekil }
    case 'yok':
      return d
  }
}

export const flowTrace: ExerciseTemplate = {
  code: 'flow_trace',
  title: 'Kuralları izle',
  domains: ['cognitive_flexibility', 'inhibition'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç kural yazılı */
      kural: scale(l, 3, 6),
      /** Başlangıç sayısı aralığının üst ucu */
      baslangic: scale(l, 9, 30),
      /** Şekil değiştiren eylemler 3. düzeyden sonra */
      sekilEylemi: l >= 3,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const kuralSayisi = p['kural'] as number
    const ustBaslangic = p['baslangic'] as number
    const sekilEylemi = p['sekilEylemi'] as boolean

    const baslangic: Durum = {
      sayi: rng.int(2, ustBaslangic),
      sekil: rng.pick(AKIS_SEKIL),
    }

    const rastgeleEylem = (): Eylem => {
      const havuz: Eylem['tur'][] = sekilEylemi
        ? ['carp', 'ekle', 'cikar', 'sekil', 'yok']
        : ['carp', 'ekle', 'cikar', 'yok']
      const tur = rng.pick(havuz)
      if (tur === 'carp') return { tur: 'carp', k: rng.int(2, 3) }
      if (tur === 'ekle') return { tur: 'ekle', k: rng.int(1, 9) }
      if (tur === 'cikar') return { tur: 'cikar', k: rng.int(1, 9) }
      if (tur === 'sekil') return { tur: 'sekil', sekil: rng.pick(AKIS_SEKIL) }
      return { tur: 'yok' }
    }

    /*
     * YOL ÖNCE KURULUYOR, kurallar sonra.
     *
     * İlk kurgu kuralları rastgele üretip sonra izliyordu ve 1.
     * kuralın "bitir"e gitmesi hiç de nadir değildi: altı kurallık
     * bir sayfada çocuk tek bir kural okuyup bitiriyordu. Sayfa
     * üretiliyordu, sınamalardan geçiyordu, ve ölçtüğü şey hiçbir
     * şeydi.
     *
     * Burada önce hangi kuralların sırayla ziyaret edileceği
     * seçiliyor; sonra her ziyaret edilen kuralın koşulu, O ANKİ
     * duruma göre istenen dalı doğru çıkaracak şekilde yazılıyor.
     * Yol üzerinde olmayan kurallar serbest — çocuğun okuyup eleyeceği
     * çeldiriciler onlar.
     */
    const enAzAdim = Math.max(2, Math.ceil(kuralSayisi * 0.6))
    const araKurallar = rng
      .shuffle(Array.from({ length: kuralSayisi - 1 }, (_, i) => i + 2))
      .slice(0, Math.min(kuralSayisi - 1, enAzAdim - 1))
      .sort((a, b) => a - b)
    const yolKurallari = [1, ...araKurallar]

    /** Koşulu, verilen durumda istenen sonucu verecek şekilde seç */
    const kosulKur = (d: Durum, istenen: boolean): Kosul => {
      const secenekler: Kosul[] = []
      // Şekil koşulu: doğru için mevcut şekil, yanlış için başkası
      if (istenen) secenekler.push({ tur: 'sekil', sekil: d.sekil })
      else {
        const baska = AKIS_SEKIL.filter((x) => x !== d.sekil)
        secenekler.push({ tur: 'sekil', sekil: rng.pick(baska) })
      }
      // Eşik koşulu: sayının altında / üstünde bir eşik
      if (istenen && d.sayi > 2) secenekler.push({ tur: 'buyuk', esik: rng.int(1, d.sayi - 1) })
      if (!istenen) secenekler.push({ tur: 'buyuk', esik: rng.int(d.sayi, d.sayi + 8) })
      // Çiftlik koşulu yalnızca zaten uyuyorsa: zorlanamıyor
      if ((d.sayi % 2 === 0) === istenen) secenekler.push({ tur: 'cift' })
      return rng.pick(secenekler)
    }

    /** Sayıyı 1–99 aralığında tutan eylem */
    const guvenliEylem = (d: Durum): Eylem => {
      const havuz: Eylem['tur'][] = sekilEylemi
        ? ['carp', 'ekle', 'cikar', 'sekil', 'yok']
        : ['carp', 'ekle', 'cikar', 'yok']
      const uygun = havuz.filter((t) => {
        if (t === 'carp') return d.sayi * 2 <= 99
        if (t === 'cikar') return d.sayi - 1 >= 1
        return true
      })
      const tur = rng.pick(uygun)
      if (tur === 'carp') {
        const enBuyukKat = Math.floor(99 / d.sayi)
        return { tur: 'carp', k: rng.int(2, Math.min(3, enBuyukKat)) }
      }
      if (tur === 'ekle') return { tur: 'ekle', k: rng.int(1, Math.min(9, 99 - d.sayi)) }
      if (tur === 'cikar') return { tur: 'cikar', k: rng.int(1, Math.min(9, d.sayi - 1)) }
      if (tur === 'sekil') {
        /*
         * Şekli DEĞİŞTİREN bir eylem seçiliyor: aynı şekle çevirmek
         * "hiçbir şey yapma"nın uzun yazılışı ve çocuğa bir adım
         * okutup hiçbir şey vermiyor.
         */
        return { tur: 'sekil', sekil: rng.pick(AKIS_SEKIL.filter((x) => x !== d.sekil)) }
      }
      return { tur: 'yok' }
    }

    const rastgeleKosul = (): Kosul => {
      const t = rng.int(0, 2)
      if (t === 0) return { tur: 'sekil', sekil: rng.pick(AKIS_SEKIL) }
      if (t === 1) return { tur: 'buyuk', esik: rng.int(5, Math.max(6, ustBaslangic)) }
      return { tur: 'cift' }
    }

    /* Yol dışı kurallar: ileri hedefli, serbest */
    const serbestHedef = (i: number): number => {
      const secenekler = [0]
      for (let j = i + 1; j <= kuralSayisi; j++) secenekler.push(j)
      return rng.pick(secenekler)
    }
    const kurallar: Kural[] = Array.from({ length: kuralSayisi }, (_, i) => {
      /*
       * Iki dali da "hicbir sey yapma, bitir" olan kurallar cikiyordu.
       * Sayfada bos bir satir: cocuk okuyor, hicbir sey ogrenmiyor,
       * ve kurali kendi yanlis okudugunu saniyor. En az bir dal is
       * yapsin.
       */
      let evet = rastgeleEylem()
      let hayir = rastgeleEylem()
      if (evet.tur === 'yok' && hayir.tur === 'yok') {
        evet = { tur: 'ekle', k: rng.int(1, 9) }
      }
      return {
        kosul: rastgeleKosul(),
        evet: { eylem: evet, hedef: serbestHedef(i + 1) },
        hayir: { eylem: hayir, hedef: serbestHedef(i + 1) },
      }
    })

    /* Yol üzerindeki kuralları duruma göre yaz */
    /** Kural numarası → izlenen dal (true: evet). Temizlikte korunuyor. */
    const yolDali = new Map<number, boolean>()
    let durum = baslangic
    for (let adim = 0; adim < yolKurallari.length; adim++) {
      const no = yolKurallari[adim]!
      const kural = kurallar[no - 1]!
      const evetDali = rng.bool()
      kural.kosul = kosulKur(durum, evetDali)
      const dal = evetDali ? kural.evet : kural.hayir
      dal.eylem = guvenliEylem(durum)
      dal.hedef = adim + 1 < yolKurallari.length ? yolKurallari[adim + 1]! : 0
      durum = uygula(dal.eylem, durum)
      /*
       * Gidilmeyen dalın hedefi de İLERİ olmalı: geriye atlayan bir
       * dal, çocuk yanlış okuduğunda sonsuz döngüye giriyor ve
       * sayfada çıkışı olmayan bir yol beliriyor.
       */
      const oteki = evetDali ? kural.hayir : kural.evet
      oteki.hedef = serbestHedef(no)
      yolDali.set(no, evetDali)
    }

    /*
     * Okunmaz satırları temizle.
     *
     * İkisi de sınamalardan çıktı, ikisi de sayfada duran ama hiçbir
     * şey anlatmayan satırlar:
     *
     *  - Her iki dalı da "hiçbir şey yapma" olan kural. Çocuk okuyor,
     *    hiçbir şey öğrenmiyor ve kuralı kendi yanlış okuduğunu
     *    sanıyor.
     *  - "Eğer şekil üçgen ise: şekli üçgen yap." Koşulun doğru
     *    olduğu dalda şekli zaten o olan şeye çevirmek.
     *
     * YALNIZCA İZLENMEYEN DALLAR düzeltiliyor. İzlenen dalın eylemini
     * değiştirmek sayıyı değiştirir, sayı sonraki koşulu değiştirir ve
     * yol kayar — kurgunun kendisini bozardı.
     */
    for (let i = 0; i < kuralSayisi; i++) {
      const kural = kurallar[i]!
      const korunan = yolDali.get(i + 1)
      if (
        kural.kosul.tur === 'sekil' &&
        kural.evet.eylem.tur === 'sekil' &&
        kural.evet.eylem.sekil === kural.kosul.sekil &&
        korunan !== true
      ) {
        kural.evet.eylem = { tur: 'ekle', k: rng.int(1, 9) }
      }
      if (kural.evet.eylem.tur === 'yok' && kural.hayir.eylem.tur === 'yok') {
        if (korunan !== false) kural.hayir.eylem = { tur: 'ekle', k: rng.int(1, 9) }
        else kural.evet.eylem = { tur: 'ekle', k: rng.int(1, 9) }
      }
    }

    /*
     * Cevap, kuralları BAĞIMSIZ izleyerek çıkarılıyor.
     *
     * Kurgu sırasında tutulan `durum` da aynı sonucu veriyor ama onu
     * cevap diye yazmak, kurgunun kendisini doğrulamak olurdu: yol
     * kurulumunda bir hata olsa cevap da aynı hatayı taşır ve hiçbir
     * şey fark etmezdik. Burada sayfadaki kurallar, çocuğun izleyeceği
     * gibi izleniyor.
     */
    const izle = (): { son: Durum; yol: number[] } => {
      let d = baslangic
      const gidilen: number[] = []
      let k = 1
      let guvenlik = 0
      while (k !== 0 && guvenlik++ < kuralSayisi + 2) {
        gidilen.push(k)
        const kural = kurallar[k - 1]!
        const dal = kosulDogru(kural.kosul, d) ? kural.evet : kural.hayir
        d = uygula(dal.eylem, d)
        k = dal.hedef
      }
      return { son: d, yol: gidilen }
    }

    const { son, yol } = izle()

    const satirlar = kurallar.map((kr, i) => {
      const n = i + 1
      return (
        `${n}) Eğer ${kosulMetni(kr.kosul)}: ${eylemMetni(kr.evet.eylem)}, ` +
        `${hedefMetni(kr.evet.hedef)}. Değilse: ${eylemMetni(kr.hayir.eylem)}, ` +
        `${hedefMetni(kr.hayir.hedef)}.`
      )
    })

    const render: RenderNode[] = [
      {
        t: 'text',
        value: `Başlangıç: ${SEKIL_ADI[baslangic.sekil]}, üstünde ${baslangic.sayi} yazıyor.`,
        size: 'md',
        bold: true,
      },
      /*
       * Baslangic nesnesi `key` dugumunde: sekil ve ustundeki sayi
       * yan yana.
       *
       * Ilk kurguda `scatter` idi ve kocaman bos bir kutunun
       * ortasinda kucucuk bir daire duruyordu — ustelik sayi hic
       * cizilmiyordu, cunku `scatter` bir ogede hem sekil hem metin
       * tasiyamiyor. Sayfaya bakinca gorundu.
       */
      {
        t: 'key',
        pairs: [{ fromShape: baslangic.sekil, to: String(baslangic.sayi) }],
      },
      { t: 'text', value: satirlar.join('  '), size: 'md' },
      {
        t: 'rows',
        items: [{ label: 'Son sayı:', blank: true, id: 'sayi' }],
        columns: 1,
      },
      {
        t: 'rows',
        items: [{ label: 'Son şekil:', blank: true, id: 'sekil' }],
        columns: 1,
        palette: AKIS_SEKIL.map((s) => ({ shape: s })),
      },
    ]

    return {
      code: 'flow_trace',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        '1. kuraldan başla. Her kuralda önce koşula bak, sonra sana söyleneni ' +
        'yap ve gönderildiğin kurala git. "Bitir" dediğinde dur.',
      spokenPrompt:
        'Kuralları sırayla değil, SANA SÖYLENDİĞİ GİBİ izleyeceksin: bir kural ' +
        'seni ileriye atlatabilir. Her adımda sayının ve şeklin son hâlini aklında tut.',
      render,
      solution: { sayi: String(son.sayi), sekil: son.sekil, yol: yol.join('-') },
      grading: 'auto',
      estimatedSeconds: 30 + kuralSayisi * 15,
      itemCount: 2,
    }
  },
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { sayi: string; sekil: string }
    const verilen = Array.isArray(answer) ? answer : []
    const oku = (i: number): string | null => {
      const h = verilen[i]
      return h === undefined || h === null || String(h).trim() === '' ? null : String(h).trim()
    }
    const items: ItemResult[] = [
      { id: 'sayi', beklenen: sol.sayi, verilen: oku(0), dogru: oku(0) === sol.sayi },
      {
        id: 'sekil',
        beklenen: sol.sekil,
        verilen: oku(1),
        dogru: oku(1) !== null && foldTr(oku(1)!) === foldTr(sol.sekil),
      },
    ]
    const dogru = items.filter((x) => x.dogru).length
    return {
      correct: dogru,
      total: 2,
      accuracy: dogru / 2,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { sayi: string; sekil: string; yol: string }
    return `sayı ${sol.sayi}, şekil ${SEKIL_ADI[sol.sekil as ShapeName]}   (izlenen kurallar: ${sol.yol})`
  },
}
